import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader, Route, Search, TriangleAlert } from 'lucide-react';
import { ESTADOS_VIAJE, type EstadoViaje } from '@tms/contratos';
import { api } from '../api';
import { ChipEstado } from '../components/ChipEstado';
import { VIAJE } from '../components/estados';
import { fmtFechaHora, fmtNum } from '../util/format';
import { useCatalogos } from '../util/catalogos';
import { useCarga, useDebounce } from '../util/hooks';

export function ViajesPagina() {
  const navegar = useNavigate();
  const catalogos = useCatalogos();
  const [params] = useSearchParams();
  const estadoInicial = params.get('estado');
  const [estado, setEstado] = useState<'' | EstadoViaje>(ESTADOS_VIAJE.find((e) => e === estadoInicial) ?? '');
  const [empresa, setEmpresa] = useState('');
  const [negocio, setNegocio] = useState('');
  const [texto, setTexto] = useState('');
  const [sinRuta, setSinRuta] = useState(params.get('sinRuta') === 'true');
  const q = useDebounce(texto.trim(), 300);

  const { datos, cargando, error, recargar } = useCarga(
    () =>
      api.viajes({ estado: estado || undefined, empresa: empresa || undefined, unidadNegocio: negocio || undefined, sinRuta: sinRuta || undefined, q: q || undefined, tamanio: 200 }),
    [estado, empresa, negocio, sinRuta, q],
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">OPERACIONES / VIAJES</div>
          <h1>Viajes</h1>
        </div>
        <span className="list-count">{datos ? `${datos.total} viaje${datos.total === 1 ? '' : 's'}` : ''}</span>
      </div>

      <div className="toolbar">
        <label className="field grow">
          <span>Buscar</span>
          <div className="search boxed">
            <Search aria-hidden />
            <input placeholder="Viaje, cliente, patente, chofer o ciudad" value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
        </label>
        <label className="field">
          <span>Estado</span>
          <select value={estado} onChange={(e) => setEstado(e.target.value as '' | EstadoViaje)}>
            <option value="">Todos</option>
            {ESTADOS_VIAJE.map((e) => (
              <option key={e} value={e}>
                {VIAJE[e].etiqueta}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Empresa</span>
          <select value={empresa} onChange={(e) => setEmpresa(e.target.value)}>
            <option value="">Todas</option>
            {catalogos.empresas.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Unidad de negocio</span>
          <select value={negocio} onChange={(e) => setNegocio(e.target.value)}>
            <option value="">Todas</option>
            {catalogos.unidadesNegocio.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label className="check-field">
          <input type="checkbox" checked={sinRuta} onChange={(e) => setSinRuta(e.target.checked)} />
          Solo viajes sin ruta
        </label>
        <button type="button" className="text-button" onClick={() => { setEstado(''); setEmpresa(''); setNegocio(''); setTexto(''); setSinRuta(false); }}>
          Limpiar filtros
        </button>
      </div>

      {error && (
        <div className="banner error" role="alert">
          <TriangleAlert aria-hidden />
          {error}
          <button type="button" className="btn-sm" onClick={recargar}>Reintentar</button>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table className="tabla">
            <thead>
              <tr>
                <th>Viaje</th>
                <th>Estado</th>
                <th>Cliente</th>
                <th>Empresa · negocio</th>
                <th>Troncal · ruta</th>
                <th>Tractor</th>
                <th>Chofer</th>
                <th>Planificado</th>
                <th>Salida real</th>
                <th>Paradas</th>
                <th className="num">Alertas</th>
                <th className="num">km GPS</th>
              </tr>
            </thead>
            <tbody>
              {datos?.items.map((v) => (
                <tr
                  key={v.id}
                  className="click"
                  tabIndex={0}
                  onClick={() => navegar(`/viajes/${v.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navegar(`/viajes/${v.id}`)}
                >
                  <td>
                    <Link to={`/viajes/${v.id}`} onClick={(e) => e.stopPropagation()}>
                      <strong>{v.idMara}</strong>
                    </Link>
                  </td>
                  <td>
                    <ChipEstado tipo="viaje" valor={v.estado} />
                  </td>
                  <td>{v.cliente ?? '—'}</td>
                  <td>
                    {v.empresa ?? '—'}
                    <small>{v.unidadNegocio ?? '—'}</small>
                  </td>
                  <td>
                    {v.troncal.nombre}
                    <small>
                      {v.ruta ? (
                        v.ruta.nombre
                      ) : (
                        <span className="amber" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <TriangleAlert aria-hidden size={12} />
                          Sin ruta
                        </span>
                      )}
                    </small>
                  </td>
                  <td>
                    {v.tractor.patente ?? '—'}
                    <small>#{v.tractor.interno ?? '—'}</small>
                  </td>
                  <td>{v.choferes[0]?.nombre ?? '—'}</td>
                  <td>{fmtFechaHora(v.fechaHoraPlanificada)}</td>
                  <td>{fmtFechaHora(v.salidaReal)}</td>
                  <td>
                    {v.paradasTotal === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      <>
                        <span className="mini-bar" aria-hidden>
                          <span style={{ width: `${((v.paradasCumplidas + v.paradasExcedidas) / v.paradasTotal) * 100}%` }} />
                        </span>
                        {v.paradasCumplidas + v.paradasExcedidas}/{v.paradasTotal}
                        {(v.paradasExcedidas > 0 || v.paradasOmitidas > 0) && (
                          <small>
                            {v.paradasExcedidas > 0 && <span className="amber">{v.paradasExcedidas} excedida{v.paradasExcedidas > 1 ? 's' : ''} </span>}
                            {v.paradasOmitidas > 0 && <span className="red">{v.paradasOmitidas} omitida{v.paradasOmitidas > 1 ? 's' : ''}</span>}
                          </small>
                        )}
                      </>
                    )}
                  </td>
                  <td className="num">{v.alertasAbiertas > 0 ? <span className="chip red">{v.alertasAbiertas}</span> : '0'}</td>
                  <td className="num">{v.kmGps === null ? '—' : fmtNum(v.kmGps, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cargando && !datos && (
          <div className="skeleton">
            <Loader className="spin" aria-hidden size={16} />
            Cargando viajes…
          </div>
        )}
        {datos && datos.items.length === 0 && (
          <div className="vacio">
            <Route aria-hidden />
            No hay viajes con esos filtros.
          </div>
        )}
      </div>
    </div>
  );
}
